import axios from '../axiosConfig'
import { getEnv } from '@utils/env'
import type { DashboardPeriod } from './finance.api'

const BUSINESS_LAYER_API = getEnv('REACT_APP_BUSINESS_LAYER_DASHBOARD')

interface ApiResponse<T> {
  statusCode: number
  message: string
  data: T
}

export interface RecareQueryParams {
  clinicId: string
  date: string
  period: DashboardPeriod
}

export interface RecareKpiData {
  recareRate: string
  patientsDue: string
  scheduled: string
  scheduledOver: string
  overdue: string
  unscheduledValue: string
  unscheduledCount: number
  comparisonLabel: string
  recareRateDelta: string
  recareRateTrend: 'up' | 'down'
  patientsDueDelta: string
  patientsDueTrend: 'up' | 'down'
  scheduledDelta: string
  scheduledTrend: 'up' | 'down'
  overdueDelta: string
  overdueTrend: 'up' | 'down'
}

export interface RecareOverviewResult {
  period: DashboardPeriod
  periodStart: string
  periodEnd: string
  kpis: RecareKpiData
  goals: { recareRateGoalPct: number }
}

export interface RecareChartPoint {
  label: string
  recareRate: number
}

export interface RecareChartConfig {
  title: string
  data: RecareChartPoint[]
  yAxisDomain: [number, number]
  yAxisTicks: number[]
}

export interface RecarePatientRow {
  id: string
  patient: string
  lastVisit: string
  dueDate: string
  daysOverdue: string
  hygienist: string
  interval: string
  status: 'Scheduled' | 'Overdue' | 'Not scheduled'
}

export interface RecareOverdueAgingRow {
  id: string
  agingBucket: string
  patients: number
  potentialValue: string
}

export interface RecareByHygienistRow {
  id: string
  hygienist: string
  due: number
  scheduled: number
  completed: number
  recareRate: string
}

export interface RecareByTimePeriodRow {
  id: string
  timePeriod: string
  patientsDue: number
  scheduled: number
  recareRate: string
}

export interface RecareDetailResult {
  period: DashboardPeriod
  periodStart: string
  periodEnd: string
  kpis: RecareKpiData
  chart: RecareChartConfig
  patients: RecarePatientRow[]
  overdueAging: RecareOverdueAgingRow[]
  byHygienist: RecareByHygienistRow[]
  byTimePeriod: RecareByTimePeriodRow[]
}

const recareGet = <T>(
  path: string,
  params: RecareQueryParams,
  options?: { signal?: AbortSignal },
): Promise<T> =>
  axios
    .get<
      ApiResponse<T>
    >(`${BUSINESS_LAYER_API}/api/v1/dashboard/recare/${path}`, { params, signal: options?.signal })
    .then((response) => response.data.data)

export const getRecareOverview = (
  params: RecareQueryParams,
  options?: { signal?: AbortSignal },
): Promise<RecareOverviewResult> => recareGet('overview', params, options)

export const getRecareDetail = (
  params: RecareQueryParams,
  options?: { signal?: AbortSignal },
): Promise<RecareDetailResult> => recareGet('detail', params, options)
